import { toObjectMap } from "@@/utilities/dictionary";
import type { ZodType } from "zod";
import { z } from "zod";
import { assertNever } from "../assert-never";
import { translation } from "../translations/translation";
import { units } from "../units-and-quantities/unit";
import type {
    DurationInDays,
    DurationInHours,
    DurationInMinutes,
    DurationInMs,
    DurationInSeconds,
} from "./time-spans";
import {
    inDays,
    inHours,
    inMilliseconds,
    inMinutes,
    inSeconds,
} from "./time-spans";

const timeGranularities = [
    "NIGHT",
    "DAY",
    "HOUR",
    "MINUTE",
    "SECOND",
    "MILLISECOND",
] as const;
type TimeGranularity = (typeof timeGranularities)[number];
const timeGranularity = toObjectMap<TimeGranularity>([...timeGranularities]);

const timeGranularityZodSchema = z.enum(timeGranularities);

function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "HOUR">,
): DurationInHours;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "DAY">,
): DurationInDays;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "NIGHT">,
): DurationInDays;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "MINUTE">,
): DurationInMinutes;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "SECOND">,
): DurationInSeconds;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: Extract<TimeGranularity, "MILLISECOND">,
): DurationInMs;
function convertTimeByGranularity(
    value: number,
    from: TimeGranularity,
    to: TimeGranularity,
) {
    switch (to) {
        case "DAY":
        case "NIGHT":
            switch (from) {
                case "DAY":
                case "NIGHT":
                    return value;
                case "HOUR":
                    return inDays({ days: value as DurationInDays });
                case "MINUTE":
                    return inDays({ minutes: value as DurationInMinutes });
                case "SECOND":
                    return inDays({ seconds: value as DurationInSeconds });
                case "MILLISECOND":
                    return inDays({ ms: value as DurationInMs });
                default:
                    return assertNever(from);
            }
        case "HOUR":
            switch (from) {
                case "DAY":
                case "NIGHT":
                    return inHours({ days: value as DurationInDays });
                case "HOUR":
                    return value;
                case "MINUTE":
                    return inHours({ minutes: value as DurationInMinutes });
                case "SECOND":
                    return inHours({ seconds: value as DurationInSeconds });
                case "MILLISECOND":
                    return inHours({ ms: value as DurationInMs });
                default:
                    return assertNever(from);
            }
        case "MINUTE":
            switch (from) {
                case "DAY":
                case "NIGHT":
                    return inMinutes({ days: value as DurationInDays });
                case "HOUR":
                    return inMinutes({ hours: value as DurationInHours });
                case "MINUTE":
                    return value;
                case "SECOND":
                    return inMinutes({ seconds: value as DurationInSeconds });
                case "MILLISECOND":
                    return inMinutes({ ms: value as DurationInMs });
                default:
                    return assertNever(from);
            }
        case "SECOND":
            switch (from) {
                case "DAY":
                case "NIGHT":
                    return inSeconds({ days: value as DurationInDays });
                case "HOUR":
                    return inSeconds({ hours: value as DurationInHours });
                case "MINUTE":
                    return inSeconds({ minutes: value as DurationInMinutes });
                case "SECOND":
                    return value;
                case "MILLISECOND":
                    return inSeconds({ ms: value as DurationInMs });
                default:
                    return assertNever(from);
            }
        case "MILLISECOND":
            switch (from) {
                case "DAY":
                case "NIGHT":
                    return inMilliseconds({ days: value as DurationInDays });
                case "HOUR":
                    return inMilliseconds({ hours: value as DurationInHours });
                case "MINUTE":
                    return inMilliseconds({
                        minutes: value as DurationInMinutes,
                    });
                case "SECOND":
                    return inMilliseconds({
                        seconds: value as DurationInSeconds,
                    });
                case "MILLISECOND":
                    return value;
                default:
                    return assertNever(from);
            }
        default:
            return assertNever(to);
    }
}

const convertTimeByGranularityWorkaround = (
    value: number,
    from: TimeGranularity,
    to: TimeGranularity,
) => {
    switch (to) {
        case "HOUR":
            return convertTimeByGranularity(value, from, "HOUR");
        case "MINUTE":
            return convertTimeByGranularity(value, from, "MINUTE");
        case "DAY":
            return convertTimeByGranularity(value, from, "DAY");
        case "NIGHT":
            return convertTimeByGranularity(value, from, "NIGHT");
        case "MILLISECOND":
            return convertTimeByGranularity(value, from, "MILLISECOND");
        case "SECOND":
            return convertTimeByGranularity(value, from, "SECOND");
        default:
            return 0;
    }
};

const addMs = (val1: DurationInMs, val2: DurationInMs): DurationInMs => {
    return (val1 + val2) as DurationInMs;
};

const granularityTranslatable = (
    granularity: TimeGranularity,
    format: "plural" | "short" | "singular" = "short",
) => {
    switch (granularity) {
        case "DAY":
            return translation({
                sv: units.day.name.sv[format],
                en: units.day.name.en[format],
            });
        case "NIGHT":
            return translation({
                sv: units.night.name.sv[format],
                en: units.night.name.en[format],
            });
        case "HOUR":
            return translation({
                sv: units.hour.name.sv[format],
                en: units.hour.name.en[format],
            });
        case "MILLISECOND":
            return translation({
                sv: units.millisecond.name.sv[format],
                en: units.millisecond.name.en[format],
            });
        case "MINUTE":
            return translation({
                sv: units.minute.name.sv[format],
                en: units.minute.name.en[format],
            });
        case "SECOND":
            return translation({
                sv: units.second.name.sv[format],
                en: units.second.name.en[format],
            });
        default:
            assertNever(granularity);
    }
};

type DurationWithGranularity = {
    value: number;
    granularity: TimeGranularity;
};

const durationWithGranularityZodObject = z.object({
    value: z.number().int().positive(),
    granularity: timeGranularityZodSchema,
}) satisfies ZodType<DurationWithGranularity>;

export {
    addMs,
    convertTimeByGranularity,
    convertTimeByGranularityWorkaround,
    durationWithGranularityZodObject,
    granularityTranslatable,
    timeGranularities,
    timeGranularity,
    timeGranularityZodSchema,
};
export type { DurationWithGranularity, TimeGranularity };
